/* eslint-disable sonarjs/no-identical-functions */
/* eslint-disable react/jsx-props-no-spreading */
import React from "react";
import styled, { css } from "styled-components";

import {
    Colors,
    FontFamilies,
    FontSizes,
    FontWeights,
    LineHeights,
    Spacing,
    Radii,
    Durations,
    BorderWidths
} from "@paclife/plds.tokens";
import Ripple from "./Ripple";
import CircularProgress from "common/CircularProgress/CircularProgress";

// these are base styles that can be built upon for specific button use cases
const StyledButtonBase = styled.button`
    // many of these properties are simply resetting values
    // much of this is inspired by Material-UI's ButtonBase
    display: inline-flex;
    align-items: center;
    justify-content: center;
    position: relative;
    overflow: hidden;
    --webkit-tap-highlight-color: transparent;
    background-color: transparent;
    outline: 0; // removes the focus ring - remember to add event listeners for people navigating using keyboards (for accessbility reasons)
    border: 0;
    margin: 0; // removes the margin in Safari
    padding: 0; // removes the padding in Firefox
    border-radius: 0;
    cursor: pointer;
    user-select: none;
    vertical-align: middle;
    --moz-appearance: none;
    --webkit-appearance: none;
    text-decoration: none; // to override the default style of a native <a /> element
    letter-spacing: normal; // to override the default style of a native <a /> element
    color: inherit;
    &::-moz-focus-inner {
        border-style: none; // removes Firefox dotted outline
    }

    ${({ disabled }) =>
        disabled &&
        css`
            pointer-events: none;
            cursor: default;
        `}

    /**
    Font Styling
    Note: I am not using the Typography component here because it is difficult 
    to style the text in the hover/active states with it
  */
  font-family: ${FontFamilies.openSans};
    font-style: normal;
    font-weight: ${FontWeights[700]};
    font-size: ${FontSizes.brandBody};
    line-height: ${LineHeights[200]};

    border-radius: ${Radii.rounded};

    /* Button Sizing */
    ${({ size, fullWidth, variant }) => {
        if (variant === "icon") {
            return css`
                padding: ${Spacing[8]};

                /* there are two size possibilites: small and medium */
                width: ${size === "small" ? Spacing[32] : Spacing[40]};
                height: ${size === "small" ? Spacing[32] : Spacing[40]};
            `;
        }

        if (variant === "text") {
            if (fullWidth) {
                return css`
                    padding: ${size === "small" ? Spacing[4] : Spacing[8]};
                    width: 100%;
                `;
            }
            return css`
                padding: ${size === "small" ? Spacing[4] : Spacing[8]};
                /* negative margin here so that button text is left-aligned */
                /* negative margin equals left padding */
                margin-left: -${size === "small" ? Spacing[4] : Spacing[8]};
            `;
        }

        if (size === "large") {
            return css`
                padding: 12px ${Spacing[24]};
                width: ${fullWidth ? "100%" : "auto"};
            `;
        }

        if (size === "medium") {
            return css`
                padding: ${Spacing[8]} ${Spacing[24]};
                width: ${fullWidth ? "100%" : "auto"};
            `;
        }

        if (size === "small") {
            return css`
                padding: ${Spacing[4]} 12px;
                width: ${fullWidth ? "100%" : "auto"};
            `;
        }

        return undefined;
    }}
`;

const PrimaryButton = styled(StyledButtonBase)`
    background-color: ${Colors.blue600};
    color: ${Colors.neutral0};
    border: ${BorderWidths.regular} solid ${Colors.blue600};

    /* styling to pass to icons */
    fill: ${Colors.neutral0};
    stroke: ${Colors.neutral0};

    &:hover {
        background-color: ${Colors.blue700};
        border-color: ${Colors.blue700};
        transition: background-color ${Durations.medium}s,
            border-color ${Durations.medium}s;
    }

    &:active {
        background-color: ${Colors.blue800};
        border-color: ${Colors.blue800};
    }

    &:focus-visible {
        border-color: ${Colors.neutral0};
        box-shadow: 0px 0px 0px 2px ${Colors.primary};
    }

    /* dark mode colors */
    ${({ dark }) =>
        dark &&
        css`
            background-color: ${Colors.blue300};
            border-color: ${Colors.blue300};
            color: ${Colors.neutral1000};

            fill: ${Colors.neutral1000};
            stroke: ${Colors.neutral1000};

            &:hover {
                background-color: ${Colors.blue200};
                border-color: ${Colors.blue200};
                transition: background-color ${Durations.medium}s,
                    border-color ${Durations.medium}s;
            }

            &:active {
                background-color: ${Colors.blue100};
                border-color: ${Colors.blue100};
            }

            &:focus-visible {
                border-color: ${Colors.neutral0};
                box-shadow: 0px 0px 0px 2px ${Colors.blue300};
            }
        `}

    ${({ disabled }) =>
        disabled &&
        css`
            background-color: ${Colors.neutral100};
            border-color: ${Colors.neutral100};
            color: ${Colors.textDisabledLight};

            fill: ${Colors.textDisabledLight};
            stroke: ${Colors.textDisabledLight};
        `}
    
    ${({ loading }) =>
        loading === "true" &&
        css`
            border-color: #aac4e2;
            background-color: #aac4e2;
            color: ${Colors.neutral0};
            border: ${BorderWidths.regular} solid #aac4e2;

            /* styling to pass to icons */
            fill: ${Colors.neutral0};
            stroke: ${Colors.neutral0};
        `}
`;

const SecondaryButton = styled(StyledButtonBase)`
    background-color: transparent;
    color: ${Colors.primary};
    border: ${BorderWidths.hairline} solid ${Colors.neutral500};

    fill: ${Colors.primary};
    stroke: ${Colors.primary};

    &:hover {
        border-color: ${Colors.primary};
        color: ${Colors.blue700};
        background-color: ${Colors.blue100};
        transition: border-color ${Durations.medium}s,
            color ${Durations.medium}s, background-color ${Durations.medium}s;

        fill: ${Colors.primary};
        stroke: ${Colors.primary};
    }

    &:active {
        color: ${Colors.blue700};
        border-color: ${Colors.neutral500};
        background-color: ${Colors.neutral100};

        fill: ${Colors.blue700};
        stroke: ${Colors.blue700};
    }

    &:focus-visible {
        box-shadow: 0px 0px 0px 2px ${Colors.primary};
    }

    ${({ disabled }) =>
        disabled &&
        css`
            color: ${Colors.textDisabledLight};
            background-color: ${Colors.neutral100};

            fill: ${Colors.neutral300};
            stroke: ${Colors.neutral300};
        `}
`;

const SecondaryNeutralButton = styled(StyledButtonBase)`
    background-color: transparent;
    color: ${Colors.neutral1000};
    border: ${BorderWidths.hairline} solid ${Colors.neutral500};

    fill: ${Colors.neutral1000};
    stroke: ${Colors.neutral1000};

    &:hover {
        background-color: ${Colors.neutral100};
        transition: background-color ${Durations.medium}s;
    }

    &:active {
        background-color: ${Colors.neutral200};
    }

    &:focus-visible {
        box-shadow: 0px 0px 0px 2px ${Colors.primary};
    }

    ${({ disabled }) =>
        disabled &&
        css`
            color: ${Colors.textDisabledLight};
            background-color: ${Colors.neutral100};

            fill: ${Colors.neutral300};
            stroke: ${Colors.neutral300};
        `}
`;

// building on top of SecondaryButton styles
const TextButton = styled(SecondaryButton)`
    border: none;

    &:hover {
        /* hover styles are the same as that of SecondaryButton */
    }

    &:focus-visible {
        background-color: ${Colors.blue200};
        color: ${Colors.blue700};
        box-shadow: none;
    }

    &:active {
        background-color: ${Colors.blue100};
        color: ${Colors.blue700};
    }

    /* dark mode colors */
    ${({ dark }) =>
        dark &&
        css`
            color: ${Colors.blue300};

            fill: ${Colors.blue300};
            stroke: ${Colors.blue300};

            &:hover {
                color: ${Colors.blue200};
                background-color: ${Colors.blue900};
                transition: background-color ${Durations.medium}s,
                    color ${Durations.medium}s;

                fill: ${Colors.blue200};
                stroke: ${Colors.blue200};
            }

            &:active {
                color: ${Colors.blue100};
                background-color: ${Colors.blue800};

                fill: ${Colors.blue100};
                stroke: ${Colors.blue100};
            }

            &:focus-visible {
                background-color: ${Colors.blue800};
                color: ${Colors.blue300};

                fill: ${Colors.blue300};
                stroke: ${Colors.blue300};
            }
        `}

    ${({ disabled }) =>
        disabled &&
        css`
            background-color: transparent;
            color: ${Colors.textDisabledLight};

            fill: ${Colors.textDisabledLight};
            stroke: ${Colors.textDisabledLight};
        `}
    
    ${({ loading }) =>
        loading === "true" &&
        css`
            color: ${Colors.blue300};

            fill: ${Colors.blue300};
            stroke: ${Colors.blue300};
        `}
`;

// same styling as TextButton
// icon button size is determined in StyledButtonBase
const IconButton = styled(TextButton)``;

const DestructiveButton = styled(StyledButtonBase)`
    background-color: ${Colors.destructive};
    border-color: ${Colors.destructive};
    color: ${Colors.neutral0};

    fill: ${Colors.neutral0};
    stroke: ${Colors.neutral0};

    &:hover {
        background-color: ${Colors.red700};
        border-color: ${Colors.red700};
        transition: background-color ${Durations.medium}s,
            border-color ${Durations.medium}s;
    }

    &:active {
        background-color: ${Colors.red800};
        border-color: ${Colors.red800};
    }

    &:focus-visible {
        box-shadow: 0px 0px 0px 2px ${Colors.neutral0},
            0px 0px 0px ${Spacing[4]} ${Colors.destructive};
    }

    ${({ disabled }) =>
        disabled &&
        css`
            background-color: ${Colors.neutral300};
            border-color: ${Colors.neutral300};
        `}
`;

const Label = styled.span`
    width: 100%;
    /* display: inherit; */
    display: flex;
    align-items: inherit;
    justify-content: inherit;
    fill: inherit;
    stroke: inherit;
`;

const IconSpan = styled.span`
    display: inherit;
    align-items: inherit;
    justify-content: inherit;
    position: relative;
    fill: inherit;
    stroke: inherit;

    /* styling applied to icons */
    > * {
        /* these values are inherited from its parent button component */
        fill: inherit;
        stroke: inherit;
    }
`;

const StartIconSpan = styled(IconSpan)`
    /* only add margin if variant is not 'icon' */
    margin-right: ${({ variant }) => (variant === "icon" ? 0 : Spacing[8])};
`;

const EndIconSpan = styled(IconSpan)`
    /* only add margin if variant is not 'icon' */
    margin-left: ${({ variant }) => (variant === "icon" ? 0 : Spacing[8])};
`;

const VariantMapping = {
    primary: PrimaryButton,
    secondary: SecondaryButton,
    secondaryNeutral: SecondaryNeutralButton,
    text: TextButton,
    icon: IconButton,
    destructive: DestructiveButton
};

const Button = React.forwardRef(
    ({
        children,
        className,
        dark = false,
        disabled = false,
        endIcon = null,
        href,
        loading,
        fullWidth = false,
        startIcon = null,
        size = "medium",
        variant = "primary",
        ...others // used to receive any native HTML element props
    }) => {
        // ensure that startIcon and endIcon are the right size
        // all icons are 16px except for the medium version of the icon button variant
        const iconSizeChooser = (buttonSize, buttonVariant) => {
            if (buttonSize === "medium" && buttonVariant === "icon") {
                return 24;
            }
            return 16;
        };

        const iconSize = iconSizeChooser(size, variant);
        let startIconWithWidth = null;
        if (loading) {
            startIcon = <CircularProgress size={16} />;
            disabled = true;
        }

        if (startIcon) {
            startIconWithWidth = React.cloneElement(startIcon, {
                width: iconSize
            });
        }

        let endIconWithWidth = null;
        if (endIcon) {
            endIconWithWidth = React.cloneElement(endIcon, { width: iconSize });
        }

        // generate the right button variant to render
        const Component = VariantMapping[variant];

        return (
            <Component
                className={className}
                variant={variant}
                dark={dark}
                disabled={disabled}
                href={href}
                fullWidth={fullWidth}
                size={size}
                loading={String(loading)}
                // rule is disabled because we need to receive any native HTML element props
                {...others}
            >
                <Label>
                    {startIcon && (
                        <StartIconSpan variant={variant}>
                            {startIconWithWidth}
                        </StartIconSpan>
                    )}
                    {children}
                    {endIcon && (
                        <EndIconSpan variant={variant}>
                            {endIconWithWidth}
                        </EndIconSpan>
                    )}
                </Label>
                <Ripple />
            </Component>
        );
    }
);

export default Button;
